/**
 * Regras de tela da aba Visão geral. O backend entrega plano (Balanceamento), realizado
 * (reportado nas OPs, pela data de início) e as linhas por máquina já calculadas; aqui só se recorta por mês e
 * formato e se monta o que cada gráfico desenha. Tudo puro, para teste.
 */
import type {
  GrupoMaquina,
  VisaoGeralMaquinaLinha,
  VisaoGeralMes,
  VisaoGeralPayload,
} from '@/types/dashboards';

const DIA_MS = 86_400_000;

export function mesDoPayload(p: VisaoGeralPayload, chave: string): VisaoGeralMes | undefined {
  return p.meses.find((m) => m.key === chave);
}

/** Todos os dias da janela 16→15, em ISO. */
export function diasDoMes(mes: Pick<VisaoGeralMes, 'start' | 'end'>): string[] {
  const dias: string[] = [];
  const fim = Date.parse(`${mes.end}T00:00:00Z`);
  for (let t = Date.parse(`${mes.start}T00:00:00Z`); t <= fim; t += DIA_MS) {
    dias.push(new Date(t).toISOString().slice(0, 10));
  }
  return dias;
}

export const categoriaDoItem = (p: VisaoGeralPayload, codigo: string): string =>
  p.itens[codigo]?.categoria ?? 'Outros';

export const temPlano = (p: VisaoGeralPayload, mes: string): boolean => p.plano.meses[mes] !== undefined;

function somaPorCategoria(
  p: VisaoGeralPayload,
  valores: Record<string, number> | undefined,
  categorias: string[],
): number {
  return Object.entries(valores ?? {}).reduce(
    (total, [codigo, un]) => (categorias.includes(categoriaDoItem(p, codigo)) ? total + un : total),
    0,
  );
}

/** Plano S&OP do mês nos formatos escolhidos; `null` quando o Balanceamento não tem o mês. */
export function planoDoMes(p: VisaoGeralPayload, mes: string, categorias: string[]): number | null {
  return temPlano(p, mes) ? somaPorCategoria(p, p.plano.meses[mes], categorias) : null;
}

export function realizadoDoMes(p: VisaoGeralPayload, mes: string, categorias: string[]): number {
  return somaPorCategoria(p, p.realizado_itens[mes], categorias);
}

// ---------------------------------------------------------------- série do gráfico principal

export interface PontoSerie {
  data: string;
  /** Reportado nas OPs que começaram no dia; `null` depois do corte (ausência de dado, não de produção). */
  realizado: number | null;
  realizadoAcum: number | null;
  /** Plano do mês ÷ dias corridos da janela; `null` sem plano. */
  metaDia: number | null;
  planoAcum: number | null;
}

function realizadoPorDia(p: VisaoGeralPayload, mes: VisaoGeralMes, categorias: string[]): Map<string, number> {
  const porDia = new Map<string, number>();
  for (const r of p.realizado_diario) {
    if (r.data < mes.start || r.data > mes.end || !categorias.includes(r.categoria)) continue;
    porDia.set(r.data, (porDia.get(r.data) ?? 0) + r.un);
  }
  return porDia;
}

export function serieDoMes(p: VisaoGeralPayload, chave: string, categorias: string[]): PontoSerie[] {
  const mes = mesDoPayload(p, chave);
  if (!mes) return [];
  const plano = planoDoMes(p, chave, categorias);
  const meta = plano === null ? null : plano / mes.dias;
  const porDia = realizadoPorDia(p, mes, categorias);
  let acumulado = 0;
  return diasDoMes(mes).map((data, i) => {
    const semDado = data > p.corte;
    const un = porDia.get(data) ?? 0;
    if (!semDado) acumulado += un;
    return {
      data,
      realizado: semDado ? null : un,
      realizadoAcum: semDado ? null : acumulado,
      metaDia: meta,
      planoAcum: meta === null ? null : meta * (i + 1),
    };
  });
}

// ---------------------------------------------------------------- produção diária por formato

export type LinhaVolume = 'grande' | 'pequeno';

/**
 * Grande volume (frascos, SPGV) é 100 ml ou mais — a régua da própria planta; abaixo disso
 * é pequeno volume (ampolas, SPPV). Os dois ficam em gráficos separados porque a escala
 * não conversa: um dia de ampola de 10 ml é quase dez dias de frasco de 100 ml.
 */
export function linhaDoFormato(categoria: string): LinhaVolume {
  const ml = Number.parseFloat(categoria);
  return Number.isFinite(ml) && ml >= 100 ? 'grande' : 'pequeno';
}

/** Formatos da linha que tiveram produção ou plano no mês — formato vazio não vira linha. */
export function formatosDaLinha(p: VisaoGeralPayload, chave: string, linha: LinhaVolume): string[] {
  const mes = mesDoPayload(p, chave);
  if (!mes) return [];
  const comDado = new Set<string>();
  for (const r of p.realizado_diario) {
    if (r.data >= mes.start && r.data <= mes.end && r.un > 0) comDado.add(r.categoria);
  }
  for (const [codigo, un] of Object.entries(p.plano.meses[chave] ?? {})) {
    if (un > 0) comDado.add(categoriaDoItem(p, codigo));
  }
  return p.categorias.filter((c) => comDado.has(c) && linhaDoFormato(c) === linha);
}

/** Chave da meta do dia de um formato na série (`meta:100 ML`). */
export const chaveMeta = (formato: string): string => `meta:${formato}`;

export type PontoDiarioFormato = Record<string, string | number | null>;

/**
 * Um ponto por dia do mês: o produzido de cada formato (`null` depois do corte) e a
 * meta S&OP do dia daquele formato (plano do formato ÷ dias da janela; `null` sem plano).
 */
export function serieDiariaPorFormato(p: VisaoGeralPayload, chave: string, formatos: string[]): PontoDiarioFormato[] {
  const mes = mesDoPayload(p, chave);
  if (!mes) return [];
  const porDia = new Map<string, number>();
  for (const r of p.realizado_diario) {
    if (r.data >= mes.start && r.data <= mes.end) porDia.set(`${r.data}|${r.categoria}`, r.un);
  }
  const metas = Object.fromEntries(
    formatos.map((f) => {
      const plano = planoDoMes(p, chave, [f]);
      return [f, plano === null ? null : plano / mes.dias];
    }),
  );
  return diasDoMes(mes).map((data) => {
    const ponto: PontoDiarioFormato = { data };
    for (const f of formatos) {
      ponto[f] = data > p.corte ? null : porDia.get(`${data}|${f}`) ?? 0;
      ponto[chaveMeta(f)] = metas[f];
    }
    return ponto;
  });
}

// ---------------------------------------------------------------- KPIs

export interface KpisVisaoGeral {
  plano: number | null;
  realizado: number;
  pctDoPlano: number | null;
  /** Quanto do plano o ritmo linear pedia até o último dia fechado (no mês fechado, o mês todo). */
  esperadoAteCorte: number | null;
  /** Produzido até o último dia fechado ÷ esperado: os dias abertos ainda estão sendo reportados. */
  aderenciaRitmo: number | null;
  /** Média dos dias fechados. */
  mediaDiaria: number | null;
  /** Por dia, para fechar o plano nos dias ainda não fechados (só mês aberto). */
  necessariaDiaria: number | null;
  /** Último dia que entra no ritmo quando o mês tem dias abertos; `null` quando todo dia com dado já fechou. */
  ritmoAte: string | null;
  diasFechados: number;
  diasDecorridos: number;
  dias: number;
  emAndamento: boolean;
  ultimoDia: { data: string; un: number } | null;
}

const pct = (a: number, b: number | null): number | null => (b ? (a / b) * 100 : null);

function necessaria(plano: number | null, realizado: number, mes: VisaoGeralMes, diasFechados: number): number | null {
  const restantes = mes.dias - diasFechados;
  if (plano === null || !mes.em_andamento || restantes <= 0) return null;
  return Math.max(plano - realizado, 0) / restantes;
}

/** Dias da janela do mês até `ate` (inclusive), de 0 a todos. */
function diasDaJanelaAte(mes: VisaoGeralMes, ate: string): number {
  if (ate < mes.start) return 0;
  const fim = ate < mes.end ? ate : mes.end;
  return Math.round((Date.parse(`${fim}T00:00:00Z`) - Date.parse(`${mes.start}T00:00:00Z`)) / DIA_MS) + 1;
}

function realizadoAte(p: VisaoGeralPayload, mes: VisaoGeralMes, categorias: string[], ate: string): number {
  return p.realizado_diario.reduce(
    (total, r) => (r.data >= mes.start && r.data <= ate && r.data <= mes.end && categorias.includes(r.categoria) ? total + r.un : total),
    0,
  );
}

function ultimoDiaComProducao(serie: PontoSerie[]): { data: string; un: number } | null {
  const ultimo = [...serie].reverse().find((s) => (s.realizado ?? 0) > 0);
  return ultimo ? { data: ultimo.data, un: ultimo.realizado ?? 0 } : null;
}

export function kpisDoMes(p: VisaoGeralPayload, chave: string, categorias: string[]): KpisVisaoGeral | null {
  const mes = mesDoPayload(p, chave);
  if (!mes) return null;
  const plano = planoDoMes(p, chave, categorias);
  const realizado = realizadoDoMes(p, chave, categorias);
  // Ritmo e média só com os dias fechados: a OP é reportada no fim do processo, e os dias abertos
  // (até dois antes do corte) ainda crescem. Snapshot antigo, sem `fechado_ate`: o próprio corte.
  const fechadoAte = p.fechado_ate ?? p.corte;
  const diasFechados = Math.min(diasDaJanelaAte(mes, fechadoAte), mes.dias_decorridos);
  const temDiaAberto = diasFechados < mes.dias_decorridos;
  const realizadoFechado = temDiaAberto ? realizadoAte(p, mes, categorias, fechadoAte) : realizado;
  const esperado = plano === null ? null : (plano * diasFechados) / mes.dias;
  return {
    plano,
    realizado,
    pctDoPlano: pct(realizado, plano),
    esperadoAteCorte: esperado,
    aderenciaRitmo: pct(realizadoFechado, esperado),
    mediaDiaria: diasFechados ? realizadoFechado / diasFechados : null,
    necessariaDiaria: necessaria(plano, realizadoFechado, mes, diasFechados),
    ritmoAte: temDiaAberto ? fechadoAte : null,
    diasFechados,
    diasDecorridos: mes.dias_decorridos,
    dias: mes.dias,
    emAndamento: mes.em_andamento,
    ultimoDia: ultimoDiaComProducao(serieDoMes(p, chave, categorias)),
  };
}

// ---------------------------------------------------------------- tabela por SKU

export type SituacaoSku = 'no_plano' | 'sem_producao' | 'fora_do_plano' | 'sem_plano';

export interface LinhaSku {
  codigo: string;
  codigoDatasul: string | null;
  descricao: string;
  categoria: string;
  plano: number | null;
  realizado: number;
  diferenca: number | null;
  pctDoPlano: number | null;
  situacao: SituacaoSku;
}

export interface GrupoSku {
  categoria: string;
  plano: number | null;
  realizado: number;
  diferenca: number | null;
  pctDoPlano: number | null;
  linhas: LinhaSku[];
}

function situacao(plano: number | null, realizado: number, mesTemPlano: boolean): SituacaoSku {
  if (!mesTemPlano) return 'sem_plano';
  if (plano === null) return 'fora_do_plano';
  return realizado > 0 ? 'no_plano' : 'sem_producao';
}

function linhaSku(p: VisaoGeralPayload, codigo: string, mes: string, mesTemPlano: boolean): LinhaSku {
  const plano = p.plano.meses[mes]?.[codigo] ?? null;
  const realizado = p.realizado_itens[mes]?.[codigo] ?? 0;
  const item = p.itens[codigo];
  return {
    codigo,
    codigoDatasul: item?.codigo_datasul ?? null,
    descricao: item?.descricao ?? '',
    categoria: categoriaDoItem(p, codigo),
    plano,
    realizado,
    diferenca: plano === null ? null : realizado - plano,
    pctDoPlano: pct(realizado, plano),
    situacao: situacao(plano, realizado, mesTemPlano),
  };
}

const peso = (l: LinhaSku): number => Math.max(l.plano ?? 0, l.realizado);

export function tabelaSku(p: VisaoGeralPayload, mes: string, categorias: string[]): GrupoSku[] {
  const mesTemPlano = temPlano(p, mes);
  const codigos = new Set([...Object.keys(p.plano.meses[mes] ?? {}), ...Object.keys(p.realizado_itens[mes] ?? {})]);
  const linhas = [...codigos].map((c) => linhaSku(p, c, mes, mesTemPlano)).filter((l) => categorias.includes(l.categoria));
  return p.categorias
    .filter((c) => categorias.includes(c))
    .map((categoria) => {
      const doGrupo = linhas.filter((l) => l.categoria === categoria).sort((a, b) => peso(b) - peso(a));
      const plano = mesTemPlano ? doGrupo.reduce((s, l) => s + (l.plano ?? 0), 0) : null;
      const realizado = doGrupo.reduce((s, l) => s + l.realizado, 0);
      return {
        categoria,
        plano,
        realizado,
        diferenca: plano === null ? null : realizado - plano,
        pctDoPlano: pct(realizado, plano),
        linhas: doGrupo,
      };
    })
    .filter((g) => g.linhas.length > 0);
}

export interface DivergenciaCodigo {
  categoria: string;
  semProducao: LinhaSku[];
  foraDoPlano: LinhaSku[];
}

/**
 * Formato em que o plano cita um SKU que não produziu e a fábrica produziu outro que o
 * plano não cita — costuma ser troca de código (ex.: 111.0715 SF → 111.0716 96fr). A
 * comparação por formato não é afetada; a por SKU, sim.
 */
export function divergenciasDeCodigo(grupos: GrupoSku[]): DivergenciaCodigo[] {
  return grupos
    .map((g) => ({
      categoria: g.categoria,
      semProducao: g.linhas.filter((l) => l.situacao === 'sem_producao'),
      foraDoPlano: g.linhas.filter((l) => l.situacao === 'fora_do_plano'),
    }))
    .filter((d) => d.semProducao.length > 0 && d.foraDoPlano.length > 0);
}

// ---------------------------------------------------------------- por máquina

export function maquinasDoMes(p: VisaoGeralPayload, grupo: GrupoMaquina, mes: string): VisaoGeralMaquinaLinha[] {
  return p.maquinas.linhas
    .filter((l) => l.grupo === grupo && l.mes === mes)
    .sort((a, b) => b.base - a.base || a.maquina.localeCompare(b.maquina));
}

export interface MatrizDesvio {
  maquinas: string[];
  celulas: Record<string, Record<string, VisaoGeralMaquinaLinha>>;
}

/** Máquinas × meses, com as máquinas de maior programado no topo. */
export function matrizDesvio(p: VisaoGeralPayload, grupo: GrupoMaquina): MatrizDesvio {
  const celulas: MatrizDesvio['celulas'] = {};
  const total = new Map<string, number>();
  for (const l of p.maquinas.linhas) {
    if (l.grupo !== grupo) continue;
    (celulas[l.maquina] ??= {})[l.mes] = l;
    total.set(l.maquina, (total.get(l.maquina) ?? 0) + l.programado);
  }
  const maquinas = [...total.keys()].sort((a, b) => (total.get(b) ?? 0) - (total.get(a) ?? 0) || a.localeCompare(b));
  return { maquinas, celulas };
}
